import { ReactNode, useEffect, useId, useMemo } from 'react'
import { initialQueryState, useDebounce } from '../../../../../../../_metronic/helpers'
import { useT } from '../../../../../../../_metronic/i18n/Metronici18n'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  Input,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useQueryRequest } from '../../../core/QueryRequestProvider'
import { useQueryResponse } from '../../../core/QueryResponseProvider'
import { useListFilters } from '../../../core/ListFiltersProvider'
import { filtrosListagemAdmin } from '../../../core/_models'

type Props = {
  /** Conteúdo à direita da linha dos campos (ex.: "Nova nota"). */
  actions?: ReactNode
}

export const NotasVersaoListSearchComponent = ({ actions }: Props) => {
  const { t } = useT()
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const total = response?.totalElements ?? 0
  const idTitulo = useId()
  const idPublicada = useId()

  const opcoesPublicada: IOption<boolean>[] = useMemo(
    () => [
      { value: true, label: t('notas_versao.filtro.publicada_sim') },
      { value: false, label: t('notas_versao.filtro.publicada_nao') },
    ],
    [t]
  )

  const debouncedFilters = useDebounce(filters, 500)

  const { search, filters: filtrosQuery } = useMemo(
    () => filtrosListagemAdmin(debouncedFilters.titulo, debouncedFilters.publicada?.value),
    [debouncedFilters]
  )

  useEffect(() => {
    updateState({
      ...initialQueryState,
      search,
      filters: filtrosQuery,
      fixedSort: [{ field: 'dtPublicacao', order: 'desc' }],
    })
  }, [search, filtrosQuery, updateState])

  const activeFilters: ActiveFilter[] = [
    filters.titulo.trim() && {
      key: 'titulo',
      label: t('notas_versao.filtro.titulo'),
      value: `“${filters.titulo.trim()}”`,
      onRemove: () => setFilter('titulo', ''),
    },
    filters.publicada && {
      key: 'publicada',
      label: t('notas_versao.filtro.publicada'),
      value: filters.publicada.label,
      onRemove: () => setFilter('publicada', undefined),
    },
  ].filter((filtro): filtro is ActiveFilter => Boolean(filtro))

  return (
    <TableFilters
      actions={actions}
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? t('notas_versao.filtro.buscando')
              : t('notas_versao.filtro.total', { total })
          }
        />
      }
    >
      <TableFilter label={t('notas_versao.filtro.titulo')} htmlFor={idTitulo} wide>
        <Input
          id={idTitulo}
          placeholder={t('notas_versao.filtro.titulo_placeholder')}
          value={filters.titulo}
          onChange={(e) => setFilter('titulo', e.target.value)}
        />
      </TableFilter>
      <TableFilter label={t('notas_versao.filtro.publicada')} htmlFor={idPublicada}>
        <CustomSelect
          inputId={idPublicada}
          placeholder={t('notas_versao.filtro.publicada_placeholder')}
          onChange={(opcao?: IOption<boolean>) => setFilter('publicada', opcao ?? undefined)}
          options={opcoesPublicada}
          value={filters.publicada}
        />
      </TableFilter>
    </TableFilters>
  )
}
